import { createContext, useContext } from 'react';
import { useQuery } from '@apollo/client/react';

// TODO: We should get these values through a graphql query
import { roles as allRoles } from 'server/context/roles';

import UserVerificationsDialog from 'web/screens/dialogs/UserVerificationsDialog';

import { CURRENT_USER } from 'api/queries/userQueries';

/**
 * User context and helpers for role-aware UI.
 *
 * This module exposes:
 *  - UserContext: React Context with the current user and helpers
 *  - useUser(): Hook to access the context
 *  - UserProvider: Fetches the current user via GraphQL and provides helpers
 *
 * It relies on the centralized role definitions in `server/context/roles`,
 * where each role has a `hierarchyLevel` that we use for "at least" checks.
 */

/**
 * @typedef {Object} CurrentUser
 * @property {string} id
 * @property {string} email
 * @property {string} role  // one of the role identifiers from server/context/roles
 */

/**
 * Build a lookup map: roleId -> hierarchy level.
 * This is computed once at module import; role definitions are static.
 *
 * @type {Record<string, number>}
 */
const roleLevel = Object.fromEntries(
	allRoles.map(r => [r._id, r.hierarchyLevel])
);

/**
 * Public shape of the UserContext value.
 * @typedef {Object} UserContextValue
 * @property {boolean} userLoading - Whether the current user query is loading.
 * @property {CurrentUser|null|undefined} user - The current user object (or null/undefined).
 * @property {(requiredRoles: string|string[]) => boolean} userHasOneOfRoles - True if the user has any of the given roles.
 * @property {(minRole: string) => boolean} userHasMinRole - True if the user's role level >= the required role level.
 * @property {(role: string) => boolean} userHasRole - True if the user's role equals the provided role.
 * @property {() => Promise<void>} logout - Logs the user out and refreshes the CURRENT_USER query.
 */

/**
 * React context for user and role helpers.
 * @type {import('react').Context<UserContextValue>}
 */
export const UserContext = createContext({
	user: null,
	userLoading: false,
	userHasOneOfRoles: () => false,
	userHasMinRole: () => false,
	userHasRole: () => false,
	logout: async () => {},
});

/**
 * Convenience hook to access the UserContext.
 * @returns {UserContextValue}
 */
export const useUser = () => useContext(UserContext);

/**
 * Provider that fetches the current user and exposes role-aware helpers.
 *
 * @param {{ children: import('react').ReactNode }} props
 * @returns {import('react').ReactElement}
 */
export default function UserProvider({ children }) {
	const {
		data: { currentUser } = { currentUser: null },
		loading,
		refetch,
	} = useQuery(CURRENT_USER, {
		fetchPolicy: 'network-only',
	});

	/**
	 * Determine whether the user has ANY of the provided roles.
	 * Accepts a single role string or an array of role strings.
	 *
	 * @param {string|string[]} requiredRoles - Role id or list of role ids.
	 * @returns {boolean}
	 */
	const userHasOneOfRoles = requiredRoles => {
		if (!currentUser) return false;

		if (typeof requiredRoles === 'string') {
			return currentUser.role === requiredRoles;
		}

		if (Array.isArray(requiredRoles)) {
			return requiredRoles.includes(currentUser.role);
		}

		return false;
	};

	/**
	 * Does the current user have at least the given minimum role?
	 * Compares by hierarchy level (robust against ordering changes).
	 *
	 * @param {string} minRole - The minimum required role id.
	 * @returns {boolean}
	 * @throws {Error} If minRole is not a string or not found in role definitions.
	 */
	const userHasMinRole = minRole => {
		if (!currentUser) return false;

		if (typeof minRole !== 'string') {
			throw new Error('userHasMinRole: Role must be a string');
		}

		const minLevel = roleLevel[minRole];
		const curLevel = roleLevel[currentUser.role];

		if (minLevel == null) {
			throw new Error(
				`userHasMinRole: Role not found. Check role name: ${minRole}`
			);
		}
		if (curLevel == null) {
			// Unknown/legacy user role — treat as insufficient privilege.
			return false;
		}

		return curLevel >= minLevel;
	};

	/**
	 * Exact role match helper.
	 *
	 * @param {string} role - Role id to check.
	 * @returns {boolean}
	 */
	const userHasRole = role => currentUser?.role === role;

	/**
	 * Logs the user out via the backend endpoint and refetches CURRENT_USER.
	 *
	 * @returns {Promise<void>}
	 */
	const logout = async () => {
		try {
			const response = await fetch('/auth/logout', {
				method: 'POST',
				credentials: 'include',
				headers: { 'Content-Type': 'application/json' },
			});

			if (!response.ok) {
				throw new Error('Failed to logout');
			}

			await refetch(); // Should cause CURRENT_USER to become null
		} catch (err) {
			// You may want to surface this to a toast/notification system.
			// Keeping console for now to avoid UI coupling.
			console.error(err);
		}
	};

	return (
		<UserContext.Provider
			value={{
				userLoading: loading,
				user: currentUser,
				userHasOneOfRoles,
				userHasMinRole,
				userHasRole,
				logout,
			}}
		>
			{children}
			<UserVerificationsDialog />
		</UserContext.Provider>
	);
}
